import { Link, isRouteErrorResponse, useRouteError } from 'react-router-dom';

import { t } from '../i18n/t';

/**
 * What a failed screen shows.
 *
 * The app had no error boundary at all: a failed query left `#root` empty,
 * which looks exactly like the bundle failing to run — a failure this
 * project has shipped once already and could not tell apart from a blank
 * page. Saying so is the whole feature.
 *
 * `isRouteErrorResponse` first: react-router's own routing errors (an
 * unmatched path, a thrown `Response`) are an `ErrorResponseImpl`, not an
 * `Error` — `error instanceof Error` is false for one and `String(error)`
 * renders `[object Object]`, telling nobody anything.
 *
 * The "Go home" link is a backstop for `routes.tsx`'s per-leaf
 * `errorElement`s: this component itself can still fail to render only if
 * `AppLayout`'s own copy catches it, in which case there is no nav on screen
 * to click instead.
 */
export function RouteError() {
  const error = useRouteError();
  const detail = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : // eslint-disable-next-line @typescript-eslint/no-base-to-string, tutor/no-english -- a diagnostic, not copy
        String(error ?? '') || '[object Object]';
  return (
    <div role="alert" className="alert alert-error my-8 flex-col items-start gap-2">
      <div>
        <p className="font-medium">{t('route-error')}</p>
        {detail && <p className="mt-1 text-sm opacity-80">{detail}</p>}
      </div>
      <Link to="/" className="btn btn-sm min-h-(--hit-min)">
        {t('route-error-home')}
      </Link>
    </div>
  );
}
